import { Link, useLocation } from "react-router-dom";
import { ChartLineUp, Gear, SignOut, Pulse, Palette, Check, ChartBar, ChartPieSlice, Calculator } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useTheme, THEMES } from "../context/ThemeContext";
import { Badge } from "./ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { NotificationBell } from "./NotificationBell";

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="theme-switcher-btn"
          className="flex items-center gap-1.5 px-2 py-1.5 text-muted-foreground hover:text-foreground transition-colors duration-150 border border-border rounded-md"
          title="Change theme"
        >
          <Palette size={16} weight="bold" />
          <span
            className="w-3 h-3 rounded-full border border-border"
            style={{ background: THEMES.find((t) => t.id === theme)?.swatch[0] }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover border-border min-w-[170px]">
        {THEMES.map((t) => (
          <DropdownMenuItem
            key={t.id}
            data-testid={`theme-option-${t.id}`}
            onClick={() => setTheme(t.id)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="flex -space-x-1">
              <span className="w-3.5 h-3.5 rounded-full border border-border" style={{ background: t.swatch[0] }} />
              <span className="w-3.5 h-3.5 rounded-full border border-border" style={{ background: t.swatch[1] }} />
            </span>
            <span className="flex-1 text-sm">{t.label}</span>
            {theme === t.id && <Check size={14} className="text-[hsl(var(--primary))]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { config, user, logout } = useAuth();

  const navItems = [
    { to: "/", label: "Journal", icon: <ChartLineUp size={18} weight="bold" /> },
    { to: "/charts", label: "Charts", icon: <ChartBar size={18} weight="bold" /> },
    { to: "/analytics", label: "Analytics", icon: <ChartPieSlice size={18} weight="bold" /> },
    { to: "/calculator", label: "Calculator", icon: <Calculator size={18} weight="bold" /> },
    { to: "/settings", label: "Settings", icon: <Gear size={18} weight="bold" /> },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/70 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 h-14">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
              <Pulse size={22} weight="bold" className="text-primary" />
              <span className="font-heading font-bold text-lg tracking-tight">My trading journal</span>
            </Link>
            <nav className="flex items-center gap-1">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors duration-150 ${
                    pathname === n.to
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  }`}
                >
                  {n.icon}
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <ThemeSwitcher />
            {config && (
              <Badge
                data-testid="trading-mode-badge"
                variant="outline"
                className={`label-caps font-mono ${
                  config.trading_mode === "live" ? "border-[hsl(var(--profit))] text-profit" : "border-border text-muted-foreground"
                }`}
              >
                {config.trading_mode === "live" ? "LIVE ENABLED" : "READ-ONLY"}
              </Badge>
            )}
            <span className="text-sm text-muted-foreground font-mono" data-testid="user-email">{user?.email}</span>
            {config?.app_mode === "multi" && (
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                title="Log out"
              >
                <SignOut size={18} />
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="px-6 py-6 fade-in">{children}</main>
    </div>
  );
}
